import { TERMINAL_DATA } from '../constants/terminalConstants';
import { getCableConnectionEndpoints, getSharedTerminalGroupEndpoints } from './cableLogic';
import { insertCableInOrder } from './cableInsertion';
import { normalizeSectionForComparison } from './sectionNormalization';

const roundSection = (value) => Math.round(value * 100) / 100;

// Le famiglie mantengono funzione e geometria del terminale durante il cambio
// automatico di sezione. I codici non elencati vengono lasciati invariati.
export const AUTOMATIC_TERMINAL_FAMILIES = [
  ['PST', 'PSW', 'PSG', 'PSR', 'PSN', 'PSB', 'PSG4', 'PSY', 'PSR10', 'PSB16'],
  ['PDW', 'PDG', 'PDR', 'PDN', 'PDB', 'PDG4', 'PDY', 'PDR10'],
  ['FSR6.3', 'FS6.3'],
  ['FCR2.8', 'FCB2.8'],
  ['FCR4.8', 'FCB4.8'],
  ['FCR6.3', 'FC6.3', 'FCB6.3'],
  ['FR3', 'FB3', 'FY3'],
  ['FR4', 'FB4', 'FY4'],
  ['OR4', 'OB4', 'OY4'],
  ['OR5', 'OB5', 'OY5'],
  ['OR6', 'OB6', 'OY6'],
  ['TR', 'TB', 'TY']
];

export const evaluateTerminalSectionCompatibility = ({
  terminalCode,
  sections = [],
  terminali = TERMINAL_DATA,
  allowMultiplePotential = true,
  // Il menu propone anche terminali più grandi del cavo: la sezione sotto il
  // minimo resta segnalata a posteriori dalla validazione.
  allowBelowMin = false
}) => {
  const config = (terminali || TERMINAL_DATA)[terminalCode];
  if (!config) {
    return { isCompatible: false, reason: 'unknown', totalSection: 0 };
  }

  const normalizedSections = sections
    .map(normalizeSectionForComparison)
    .filter(section => Number.isFinite(section) && section > 0);
  const totalSection = roundSection(normalizedSections.reduce((sum, section) => sum + section, 0));

  if (config.minSection == null && config.maxSection == null) {
    return { isCompatible: true, reason: 'unbounded', totalSection };
  }

  if (config.maxSection != null && totalSection > config.maxSection) {
    return { isCompatible: false, reason: 'above-max', totalSection };
  }

  if (config.minSection != null && totalSection < config.minSection) {
    if (allowBelowMin) {
      return { isCompatible: true, reason: 'below-min-allowed', totalSection };
    }
    const canBecomeCompatible = allowMultiplePotential && config.allowsMultiple === true;
    return {
      isCompatible: canBecomeCompatible,
      reason: canBecomeCompatible ? 'multiple-potential' : 'below-min',
      totalSection
    };
  }

  return { isCompatible: true, reason: 'within-range', totalSection };
};

const withCandidateTerminal = (cable, terminalCode, side, poloIndex = null) => {
  if (poloIndex == null) {
    return { ...cable, [`terminale${side}`]: terminalCode };
  }

  return {
    ...cable,
    poli: (cable.poli || []).map((polo, index) => (
      index === poloIndex ? { ...polo, [`terminale${side}`]: terminalCode } : polo
    ))
  };
};

const buildEvaluationScope = ({ cable, cables = [], editingIndex = -1, terminalCode, side, poloIndex, settings }) => {
  const draftMarker = `terminal-compatibility-${side}-${poloIndex ?? 'cable'}`;
  const candidateCable = {
    ...withCandidateTerminal(cable, terminalCode, side, poloIndex),
    _terminalCompatibilityDraftId: draftMarker
  };

  if (editingIndex >= 0 && editingIndex < cables.length) {
    const scope = cables.map((existingCable, index) => (
      index === editingIndex ? { ...candidateCable, originalIndex: index } : existingCable
    ));
    return { scope, targetCable: scope[editingIndex] };
  }

  const scope = insertCableInOrder(candidateCable, cables, settings || {});
  const targetCable = scope.find(item => item._terminalCompatibilityDraftId === draftMarker) || scope[scope.length - 1];

  return { scope, targetCable };
};

export const getEndpointTerminalCompatibility = ({
  terminalCode,
  terminali = TERMINAL_DATA,
  endpointContext,
  settings = {},
  allowMultiplePotential = true,
  allowBelowMin = false
}) => {
  if (!endpointContext?.cable || !endpointContext?.side) {
    return evaluateTerminalSectionCompatibility({
      terminalCode,
      sections: [],
      terminali,
      allowMultiplePotential,
      allowBelowMin
    });
  }

  const { side, poloIndex = null } = endpointContext;
  const fallbackSection = poloIndex == null
    ? endpointContext.cable.sezione
    : endpointContext.cable.poli?.[poloIndex]?.sezione;
  const { scope, targetCable } = buildEvaluationScope({
    ...endpointContext,
    terminalCode,
    settings
  });
  const targetEndpoint = getCableConnectionEndpoints(targetCable).find(endpoint => (
    endpoint.side === side && (endpoint.poloIndex ?? null) === (poloIndex ?? null)
  ));

  if (!targetEndpoint) {
    return evaluateTerminalSectionCompatibility({
      terminalCode,
      sections: [fallbackSection],
      terminali,
      allowMultiplePotential,
      allowBelowMin
    });
  }

  const group = getSharedTerminalGroupEndpoints({
    targetEndpoint,
    cables: scope,
    settings: { ...settings, terminal_data: terminali }
  });
  const sections = group.length > 0 ? group.map(endpoint => endpoint.sezione) : [targetEndpoint.sezione];

  return evaluateTerminalSectionCompatibility({
    terminalCode,
    sections,
    terminali,
    allowMultiplePotential,
    allowBelowMin
  });
};

const getAutomaticFamily = (terminalCode) => (
  AUTOMATIC_TERMINAL_FAMILIES.find(family => family.includes(terminalCode)) || null
);

const replaceEndpointTerminal = (cable, side, terminalCode, poloIndex = null) => {
  if (poloIndex == null) return { ...cable, [`terminale${side}`]: terminalCode };
  return {
    ...cable,
    poli: cable.poli.map((polo, index) => (
      index === poloIndex ? { ...polo, [`terminale${side}`]: terminalCode } : polo
    ))
  };
};

export const autoAdjustTerminalsForSectionChange = ({
  cable,
  cables = [],
  editingIndex = -1,
  terminali = TERMINAL_DATA,
  settings = {}
}) => {
  if (!cable) return cable;

  const endpointTargets = Array.isArray(cable.poli)
    ? cable.poli.flatMap((_, poloIndex) => ([
      { side: 'A', poloIndex },
      { side: 'B', poloIndex }
    ]))
    : [{ side: 'A', poloIndex: null }, { side: 'B', poloIndex: null }];

  return endpointTargets.reduce((adjustedCable, { side, poloIndex }) => {
    const endpoint = poloIndex == null ? adjustedCable : adjustedCable.poli[poloIndex];
    const terminalCode = endpoint?.[`terminale${side}`];
    const family = getAutomaticFamily(terminalCode);
    if (!terminalCode || !family) return adjustedCable;

    const compatibilityFor = (candidateCode) => getEndpointTerminalCompatibility({
      terminalCode: candidateCode,
      terminali,
      endpointContext: {
        cable: adjustedCable,
        cables,
        editingIndex,
        side,
        poloIndex
      },
      settings,
      allowMultiplePotential: false
    });

    if (compatibilityFor(terminalCode).isCompatible) return adjustedCable;

    const replacement = family.find(candidateCode => (
      candidateCode !== terminalCode &&
      terminali[candidateCode] &&
      compatibilityFor(candidateCode).isCompatible
    ));

    return replacement
      ? replaceEndpointTerminal(adjustedCable, side, replacement, poloIndex)
      : adjustedCable;
  }, cable);
};
